import * as THREE from 'three';
import { Entity } from '../Entity.js';
import type { Game } from '../../game/Game.js';
import type { Circle } from '../../systems/Collision.js';
import { audio } from '../../audio/Audio.js';

/**
 * Drop table entry for enemy loot drops.
 */
export interface DropTableEntry {
  /** Part ID from PartRegistry, or null for no drop */
  partId: string | null;
  /** Weight for this drop (higher = more likely) */
  weight: number;
}

/**
 * Base class for all enemy entities.
 *
 * Enemies are hostile entities that:
 * - Have HP and can be killed
 * - Drop loot when killed (based on drop table)
 * - Have collision shapes for projectile hits
 * - Update and behave according to AI patterns
 * - Can telegraph attacks for visual clarity
 * - Can show HP bars, drop previews based on player head abilities
 *
 * Extend this class to create specific enemy types (Sentry, Drone, etc.)
 */
export abstract class Enemy extends Entity {
  protected hp: number;
  protected maxHP: number;
  protected damage: number = 0; // Base damage for this enemy
  protected dropTable: DropTableEntry[];
  protected game: Game;

  // Telegraph system
  protected isTelegraphing: boolean = false;
  protected telegraphTimer: number = 0;
  protected telegraphDuration: number = 0;
  protected originalMaterialColors: Map<number, { color: number; emissive: number }> = new Map();

  // Head ability visualizations
  private hpBarGroup: THREE.Group | null = null;
  private hpBarFill: THREE.Mesh | null = null;
  private dropPreviewMesh: THREE.Mesh | null = null;
  private cachedDrop: string | null | undefined = undefined; // undefined = not yet rolled

  /**
   * Create a new enemy.
   *
   * @param x - Starting X position
   * @param y - Starting Y position
   * @param maxHP - Maximum HP for this enemy
   * @param dropTable - Loot drop table for this enemy
   * @param game - Reference to game instance for spawning drops
   */
  constructor(
    x: number,
    y: number,
    maxHP: number,
    dropTable: DropTableEntry[],
    game: Game
  ) {
    super(x, y, 0);
    this.maxHP = maxHP;
    this.hp = maxHP;
    this.dropTable = dropTable;
    this.game = game;
  }

  /**
   * Take damage and reduce HP.
   *
   * @param amount - Amount of damage to take
   * @returns true if enemy was killed by this damage, false otherwise
   */
  takeDamage(amount: number): boolean {
    this.hp -= amount;

    // Play hit sound
    audio.hit();

    if (this.hp <= 0) {
      this.hp = 0;
      this.onDeath();
      this.destroy();
      return true;
    }
    return false;
  }

  /**
   * Get current HP.
   */
  getHP(): number {
    return this.hp;
  }

  /**
   * Get maximum HP.
   */
  getMaxHP(): number {
    return this.maxHP;
  }

  /**
   * Check if enemy is still alive.
   */
  isAlive(): boolean {
    return this.hp > 0;
  }

  /**
   * Called when the enemy is killed.
   *
   * Spawns loot drops based on the drop table (uses cached drop if previewed).
   * Subclasses can override this to add additional death behavior
   * (e.g., explosions, sound effects), but should call super.onDeath().
   */
  protected onDeath(): void {
    // Play death sound
    audio.enemyDie();

    // Notify game that an enemy was killed (for room clear detection)
    this.game.onEnemyKilled();

    // Spawn loot drop (use cached drop if already previewed, otherwise roll)
    const drop = this.cachedDrop !== undefined ? this.cachedDrop : this.rollDrop();
    if (drop) {
      this.game.spawnPickupById(this.position.x, this.position.y, drop);
    }
  }

  /**
   * Roll for a random drop from the drop table.
   *
   * Uses weighted random selection based on drop table weights.
   *
   * @returns Part ID to drop, or null if no drop
   */
  protected rollDrop(): string | null {
    // Calculate total weight
    const totalWeight = this.dropTable.reduce((sum, entry) => sum + entry.weight, 0);

    // Roll a random number from 0 to totalWeight
    const roll = Math.random() * totalWeight;

    // Find which entry was selected
    let currentWeight = 0;
    for (const entry of this.dropTable) {
      currentWeight += entry.weight;
      if (roll < currentWeight) {
        return entry.partId;
      }
    }

    // Fallback (shouldn't happen unless table is empty)
    return null;
  }

  /**
   * Apply difficulty scaling multipliers to this enemy.
   *
   * This should be called after spawning to scale HP and damage based on floor difficulty.
   *
   * @param hpMult - HP multiplier (e.g., 1.2 for +20% HP)
   * @param damageMult - Damage multiplier (e.g., 1.1 for +10% damage)
   */
  applyMultipliers(hpMult: number, damageMult: number): void {
    // Scale max HP and current HP
    this.maxHP = Math.floor(this.maxHP * hpMult);
    this.hp = this.maxHP;

    // Scale damage (if this enemy has a damage value)
    if (this.damage > 0) {
      this.damage = Math.floor(this.damage * damageMult);
    }
  }

  /**
   * Update enemy behavior.
   *
   * Subclasses must implement this to define enemy AI.
   *
   * @param dt - Fixed delta time in milliseconds
   */
  abstract update(dt: number): void;

  /**
   * Get the collision shape for this enemy.
   *
   * Subclasses must implement this to define their hitbox.
   *
   * @returns Circle collision shape
   */
  abstract getCollisionShape(): Circle;

  /**
   * Start telegraphing an attack.
   *
   * Stores original material colors and begins pulsing animation.
   *
   * @param duration - How long to telegraph before firing (seconds)
   */
  protected startTelegraph(duration: number): void {
    this.isTelegraphing = true;
    this.telegraphTimer = duration;
    this.telegraphDuration = duration;

    // Store original material colors
    this.originalMaterialColors.clear();
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const material = child.material as THREE.MeshStandardMaterial;
          if (material && material.color && material.emissive) {
            this.originalMaterialColors.set(child.id, {
              color: material.color.getHex(),
              emissive: material.emissive.getHex(),
            });
          }
        }
      });
    }
  }

  /**
   * End the telegraph and restore original colors.
   */
  protected endTelegraph(): void {
    this.isTelegraphing = false;
    this.telegraphTimer = 0;

    // Restore original material colors
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const original = this.originalMaterialColors.get(child.id);
          if (original) {
            const material = child.material as THREE.MeshStandardMaterial;
            material.color.setHex(original.color);
            material.emissive.setHex(original.emissive);
            material.emissiveIntensity = 0;
          }
        }
      });
    }
    this.originalMaterialColors.clear();
  }

  /**
   * Update the telegraph animation.
   *
   * Creates a pulsing white/red flash effect.
   * Call this in your update() method if you're using telegraphs.
   *
   * @param dtSec - Delta time in seconds
   * @returns true if telegraph just completed, false otherwise
   */
  protected updateTelegraph(dtSec: number): boolean {
    if (!this.isTelegraphing) return false;

    this.telegraphTimer -= dtSec;

    // Calculate pulse intensity (fast pulse that gets faster as time runs out)
    const t = this.telegraphTimer / this.telegraphDuration; // 1.0 to 0.0
    const pulseFrequency = 8 + (1 - t) * 12; // Speed up as we approach attack
    const pulse = Math.sin(this.telegraphTimer * Math.PI * pulseFrequency) * 0.5 + 0.5; // 0.0 to 1.0

    // Apply pulse to all materials
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const material = child.material as THREE.MeshStandardMaterial;
          if (material && material.emissive) {
            // Flash white/red
            const flashColor = 0xffffff; // White flash
            material.emissive.setHex(flashColor);
            material.emissiveIntensity = pulse * (1 - t * 0.5); // Intensity increases as time runs out
          }
        }
      });
    }

    // Check if telegraph completed
    if (this.telegraphTimer <= 0) {
      this.endTelegraph();
      return true; // Signal that it's time to attack
    }

    return false;
  }

  // ========== HEAD ABILITY VISUALIZATIONS ==========

  /**
   * Update visualizations based on player head abilities.
   *
   * @param showHP - Whether to show HP bar
   * @param showDrops - Whether to show drop preview
   */
  updateHeadAbilityVisuals(showHP: boolean, showDrops: boolean): void {
    // HP Bar
    if (showHP) {
      this.showHPBar();
    } else {
      this.hideHPBar();
    }

    // Drop Preview
    if (showDrops) {
      this.showDropPreview();
    } else {
      this.hideDropPreview();
    }
  }

  /**
   * Show or update the HP bar above the enemy.
   */
  private showHPBar(): void {
    if (!this.mesh) return;

    // Create HP bar if it doesn't exist
    if (!this.hpBarGroup) {
      this.hpBarGroup = new THREE.Group();

      // Background (dark)
      const bgGeometry = new THREE.PlaneGeometry(1.0, 0.12);
      const bgMaterial = new THREE.MeshBasicMaterial({
        color: 0x222222,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
      });
      const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
      bgMesh.renderOrder = 999;
      this.hpBarGroup.add(bgMesh);

      // Fill (green/red)
      const fillGeometry = new THREE.PlaneGeometry(0.96, 0.08);
      const fillMaterial = new THREE.MeshBasicMaterial({
        color: 0x66bb6a,
        depthTest: false,
      });
      this.hpBarFill = new THREE.Mesh(fillGeometry, fillMaterial);
      this.hpBarFill.position.z = 0.01;
      this.hpBarFill.renderOrder = 1000;
      this.hpBarGroup.add(this.hpBarFill);

      // Position above enemy
      this.hpBarGroup.position.y = 1.5;
      this.hpBarGroup.rotation.x = -Math.PI / 3; // Tilt to face camera

      this.mesh.add(this.hpBarGroup);
    }

    // Update HP bar fill
    if (this.hpBarFill) {
      const hpPercent = this.hp / this.maxHP;
      this.hpBarFill.scale.x = Math.max(0.01, hpPercent);
      this.hpBarFill.position.x = (hpPercent - 1) * 0.48;

      // Color based on HP percentage
      const fillMat = this.hpBarFill.material as THREE.MeshBasicMaterial;
      if (hpPercent > 0.5) {
        fillMat.color.setHex(0x66bb6a); // Green
      } else if (hpPercent > 0.25) {
        fillMat.color.setHex(0xffa726); // Orange
      } else {
        fillMat.color.setHex(0xef5350); // Red
      }
    }

    if (this.hpBarGroup) {
      this.hpBarGroup.visible = true;
    }
  }

  /**
   * Hide the HP bar.
   */
  private hideHPBar(): void {
    if (this.hpBarGroup) {
      this.hpBarGroup.visible = false;
    }
  }

  /**
   * Show or update the drop preview icon above the enemy.
   */
  private showDropPreview(): void {
    if (!this.mesh) return;

    // Pre-roll the drop if not yet done (deterministic preview)
    if (this.cachedDrop === undefined) {
      this.cachedDrop = this.rollDrop();
    }

    // Only show if there's an actual drop
    if (!this.cachedDrop) {
      this.hideDropPreview();
      return;
    }

    // Create preview icon if it doesn't exist
    if (!this.dropPreviewMesh) {
      // Small colored diamond to indicate drop
      const geometry = new THREE.OctahedronGeometry(0.15, 0);
      const material = new THREE.MeshBasicMaterial({
        color: 0xffeb3b, // Yellow for pickup indicator
        transparent: true,
        opacity: 0.7,
        depthTest: false,
      });
      this.dropPreviewMesh = new THREE.Mesh(geometry, material);
      this.dropPreviewMesh.position.set(0.6, 1.2, 0);
      this.dropPreviewMesh.renderOrder = 998;
      this.mesh.add(this.dropPreviewMesh);
    }

    this.dropPreviewMesh.visible = true;

    // Rotate the preview for visual effect
    this.dropPreviewMesh.rotation.y += 0.02;
  }

  /**
   * Hide the drop preview.
   */
  private hideDropPreview(): void {
    if (this.dropPreviewMesh) {
      this.dropPreviewMesh.visible = false;
    }
  }

  /**
   * Get the pre-rolled drop for this enemy (used by drop preview).
   * Returns the cached drop if already rolled, otherwise rolls and caches.
   */
  getPreRolledDrop(): string | null {
    if (this.cachedDrop === undefined) {
      this.cachedDrop = this.rollDrop();
    }
    return this.cachedDrop;
  }
}
